import React, { createContext, useContext, useMemo, memo } from 'react';
import ExpandableContentPrimitive from '../../atoms/expandable-content/ExpandableContent';
import FilePreviewPrimitive, {
  FilePreviewVariant,
} from '../../atoms/file-preview/FilePreview';
import Tiptap, { generateHTML } from '../../molecules/tiptap/Tiptap';
import cn from '../../utils/cn';
import {
  ActivityCard,
  ActivityCardContentVariant,
} from '../../organisms/activity-card/ActivityCard.tsx';

export enum ActivityMessagingVariant {
  DEFAULT,
  MEDIA,
  DOCUMENT,
}

type ActivityFeedItem = {
  description?: string;
  jsonContent?: string;
};

const ActivityMessagingContext = createContext<{
  variant: ActivityMessagingVariant;
}>({ variant: ActivityMessagingVariant.DEFAULT });

export const useActivityMessaging = () => useContext(ActivityMessagingContext);

const Root = memo(
  ({
    variant = ActivityMessagingVariant.DEFAULT,
    className,
    ...props
  }: Omit<React.ComponentPropsWithoutRef<typeof ActivityCard.Content>, 'variant'> & {
    variant?: ActivityMessagingVariant;
  }) => {
    return (
      <ActivityMessagingContext.Provider value={{ variant }}>
        <ActivityCard.Content
          className={cn(
            { 'gap-4': variant !== ActivityMessagingVariant.DEFAULT },
            { 'flex-row': variant === ActivityMessagingVariant.MEDIA },
            className,
          )}
          variant={ActivityCardContentVariant.BORDERLESS}
          {...props}
        />
      </ActivityMessagingContext.Provider>
    );
  },
);

Root.displayName = 'ActivityMessaging.Root';

const ExpandableContent = memo(
  ({
    className,
    children,
    ...props
  }: React.ComponentPropsWithoutRef<typeof ExpandableContentPrimitive>) => {
    const { variant } = useActivityMessaging();

    const collapsedHeight = useMemo(() => {
      if (variant !== ActivityMessagingVariant.MEDIA) {
        return undefined;
      }

      return 240;
    }, [variant]);

    return (
      <ExpandableContentPrimitive
        collapsedHeight={collapsedHeight}
        className="w-full"
        {...props}
      >
        <div className={cn('flex flex-col gap-1', className)}>{children}</div>
      </ExpandableContentPrimitive>
    );
  },
);

ExpandableContent.displayName = 'ActivityMessaging.ExpandableContent';

const Content = memo(({ activity }: { activity: ActivityFeedItem }) => {
  const htmlContent = useMemo(() => {
    if (!activity.jsonContent) {
      return null;
    }

    try {
      return generateHTML(JSON.parse(activity.jsonContent));
    } catch (error) {
      console.error('Error parsing JSON content:', error);
      return null;
    }
  }, [activity.jsonContent]);

  if (htmlContent) {
    return (
      <Tiptap.HtmlRenderer
        dangerouslySetInnerHTML={{
          __html: htmlContent,
        }}
      />
    );
  }

  return (
    <div className="whitespace-pre-wrap break-all text-xs font-normal text-neutral-600 empty:hidden">
      {activity.description}
    </div>
  );
});

Content.displayName = 'ActivityMessaging.Content';

const Title = memo(
  ({ className, ...props }: React.ComponentPropsWithoutRef<'strong'>) => (
    <strong
      className={cn('text-base font-semibold text-neutral-700', className)}
      {...props}
    />
  ),
);

Title.displayName = 'ActivityMessaging.Title';

/**
 * Note: We avoid destructuring variant-dependent props (mimeType, fileName) directly
 * in the function signature. Destructuring breaks TypeScript's discriminated union
 * narrowing — the compiler loses the link between `variant` and the other props.
 */
const FilePreview = memo(
  (props: React.ComponentPropsWithoutRef<typeof FilePreviewPrimitive>) => {
    if (props.variant === FilePreviewVariant.MEDIA) {
      return (
        <FilePreviewPrimitive.Root className={cn('size-60', props.className)} {...props}>
          <FilePreviewPrimitive.Image />
        </FilePreviewPrimitive.Root>
      );
    }

    return <FilePreviewPrimitive {...props} />;
  },
);

FilePreview.displayName = 'ActivityMessaging.FilePreview';

const ActivityMessaging = {
  Root,
  Title,
  ExpandableContent,
  Content,
  FilePreview,
} as const;

export default ActivityMessaging;
